import { useId, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import arrowDown from '../../assets/filter-arrow-down.svg';
import checkedIcon from '../../assets/filter-checked.svg';
import uncheckedIcon from '../../assets/filter-unchecked.svg';

interface Props<T extends string> {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (next: T[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  size?: 'default' | 'compact';
}

/** Controlled filter selection shared by lender Users and Products. */
export default function FilterMultiSelect<T extends string>({
  label, options, selected, onChange, open, onOpenChange, size = 'default',
}: Props<T>) {
  const optionsId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const compact = size === 'compact';
  const summary = selected.length === 0 ? 'Select' : `${label} (${selected.length} Selected)`;

  return (
    <div onKeyDown={(event) => {
      if (event.key === 'Escape' && open) {
        event.preventDefault();
        event.stopPropagation();
        onOpenChange(false);
        triggerRef.current?.focus();
      }
    }}>
      <button ref={triggerRef} type="button" aria-label={label} aria-expanded={open} aria-controls={optionsId}
        onClick={() => onOpenChange(!open)}
        className={compact
          ? `flex h-[30px] w-full min-w-0 items-center justify-between gap-2 rounded border bg-surface pl-[15px] pr-[10px] text-left text-xs font-medium leading-[18px] text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${open ? 'border-brand' : 'border-border'}`
          : 'flex h-10 w-full items-center justify-between rounded-md border border-border-input bg-surface px-3 text-left text-sm text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand'}>
        <span className={`truncate ${selected.length === 0 ? (compact ? 'text-[#c2c9d1]' : 'text-content-secondary') : ''}`}>{summary}</span>
        {compact ? <span className={`shrink-0 ${open ? "rotate-180" : ""}`}><img src={arrowDown} alt="" /></span>
          : <ChevronDown className="h-4 w-4 shrink-0 text-content-secondary" aria-hidden="true" />}
      </button>
      {open && (
        <div id={optionsId} role="group" aria-label={`${label} options`}
          className={compact
            ? 'mt-[5px] max-h-48 overflow-y-auto overscroll-contain rounded border border-border-subtle bg-surface py-1 shadow-sm'
            : 'mt-1 max-h-40 overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-sm'}>
          {options.length === 0 && <p className="px-3 py-2 text-xs text-content-secondary">No options available</p>}
          {options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <label key={option.value} className={compact
                ? 'flex cursor-pointer items-center justify-between gap-2 px-[7px] py-1 text-[11px] font-medium leading-[18px] text-content-secondary hover:bg-surface-sunken'
                : 'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-content hover:bg-surface-sunken'}>
                {compact && <span className="min-w-0 [overflow-wrap:anywhere]">{option.label}</span>}
                <span className={compact ? 'relative flex h-[15px] w-[15px] shrink-0 items-center justify-center' : 'contents'}>
                  <input type="checkbox" checked={checked}
                    onChange={() => onChange(checked ? selected.filter((value) => value !== option.value) : [...selected, option.value])}
                    className={compact
                      ? 'peer absolute inset-0 h-full w-full cursor-pointer opacity-0'
                      : 'h-4 w-4 rounded border-border text-brand focus:ring-brand'} />
                  {compact && <img src={checked ? checkedIcon : uncheckedIcon} alt="" className="pointer-events-none peer-focus-visible:rounded-full peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand" />}
                </span>
                {!compact && option.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
